import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { EmissionTier, MetricState, canonicalPeriodLabels, CanonicalPeriod } from '../../common'
import { usePropertyClimateData } from '../../../../hooks/climateData'
import { WildfireScoreBadge } from '../../common/fireRisk/utils'
import { useCurrentUser } from '../../../../hooks/users'
import ClimateMetricCard from '../../common/ClimateMetricCard'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'
import {
  BI_DAYS_LABEL,
  buildAnnualLossDisplay,
  DAMAGE_AAL_LABEL,
  FINANCIAL_BREAKDOWN_TITLE,
  formatCurrencyValue,
  formatDamageTargetLabel,
  formatPlainNumber,
  parseMetricPointY,
  REVENUE_AAL_LABEL,
  sumMetricNode,
} from '../../common/financialMetrics'

const SCORE_METRIC_KEY = 'wf:score'
const DAMAGE_AAL_METRIC_KEY = 'wf:damage_loss'
const REVENUE_AAL_METRIC_KEY = 'wf:bi_loss'
const BI_DAYS_METRIC_KEY = 'wf:bi_days'
const FIRES_METRIC_KEY = 'wf:fires'

const PERIOD_ORDER: CanonicalPeriod[] = [
  CanonicalPeriod.SHORT_TERM,
  CanonicalPeriod.MID_TERM,
  CanonicalPeriod.LONG_TERM,
]

function sortPeriods(periods: string[]): string[] {
  return [...periods].sort((left, right) => PERIOD_ORDER.indexOf(left as CanonicalPeriod) - PERIOD_ORDER.indexOf(right as CanonicalPeriod))
}

function getSucceededMetricData<T>(metric: { state?: { value?: MetricState }; data?: T | null } | undefined) {
  if (!metric || metric.state?.value !== MetricState.SUCCEEDED) return null
  return metric.data ?? null
}

export default function WildfireSummary({
  propertyId,
  emissionTier,
}: {
  propertyId: number
  emissionTier: EmissionTier
}) {
  const {
    data: scoreData,
    isLoading: scoreLoading,
    error: scoreError,
  } = usePropertyClimateData(SCORE_METRIC_KEY, { propertyId })
  const {
    data: damageAalData,
    isLoading: damageAalLoading,
    error: damageAalError,
  } = usePropertyClimateData(DAMAGE_AAL_METRIC_KEY, { propertyId })
  const {
    data: revenueAalData,
    isLoading: revenueAalLoading,
    error: revenueAalError,
  } = usePropertyClimateData(REVENUE_AAL_METRIC_KEY, { propertyId })
  const {
    data: biDaysData,
    isLoading: biDaysLoading,
    error: biDaysError,
  } = usePropertyClimateData(BI_DAYS_METRIC_KEY, { propertyId })
  const { data: firesData } = usePropertyClimateData(FIRES_METRIC_KEY, { propertyId })

  const { currencySymbol } = useCurrentUser()
  const [selectedPeriod, setSelectedPeriod] = useState<CanonicalPeriod>(CanonicalPeriod.MID_TERM)

  const scorePayload = getSucceededMetricData(scoreData)
  const damageAalPayload = getSucceededMetricData(damageAalData)
  const revenueAalPayload = getSucceededMetricData(revenueAalData)
  const biDaysPayload = getSucceededMetricData(biDaysData)
  const firesPayload = getSucceededMetricData(firesData)

  const availablePeriods = useMemo(() => {
    const keys = Array.from(
      new Set([
        ...(((scorePayload as any)?.keys?.period_canonical as string[] | undefined) ?? []),
        ...(((damageAalPayload as any)?.keys?.period_canonical as string[] | undefined) ?? []),
        ...(((revenueAalPayload as any)?.keys?.period_canonical as string[] | undefined) ?? []),
        ...(((biDaysPayload as any)?.keys?.period_canonical as string[] | undefined) ?? []),
        ...(((firesPayload as any)?.keys?.period_canonical as string[] | undefined) ?? []),
      ]),
    )

    if (!keys.length) return PERIOD_ORDER as string[]
    return sortPeriods(keys)
  }, [biDaysPayload, damageAalPayload, firesPayload, revenueAalPayload, scorePayload])

  const effectivePeriod = useMemo(() => {
    if (availablePeriods.includes(selectedPeriod)) return selectedPeriod
    if (availablePeriods.includes(CanonicalPeriod.MID_TERM)) return CanonicalPeriod.MID_TERM
    return availablePeriods[0] as CanonicalPeriod
  }, [availablePeriods, selectedPeriod])

  const scoreValue = parseMetricPointY((scorePayload as any)?.data?.[emissionTier]?.[effectivePeriod])
  const damageAalPeriodNode = (damageAalPayload as any)?.data?.[emissionTier]?.[effectivePeriod]
  const revenueAalPeriodNode = (revenueAalPayload as any)?.data?.[emissionTier]?.[effectivePeriod]
  const biDaysPeriodNode = (biDaysPayload as any)?.data?.[emissionTier]?.[effectivePeriod]
  const firesNode = (firesPayload as any)?.data?.[emissionTier]?.[effectivePeriod]

  const damageAalTotal = sumMetricNode(damageAalPeriodNode)
  const revenueAalTotal = sumMetricNode(revenueAalPeriodNode)
  const biDaysTotal = sumMetricNode(biDaysPeriodNode)
  const firesValue = sumMetricNode(firesNode)
  const totalLossDisplay = buildAnnualLossDisplay(damageAalTotal, revenueAalTotal)

  const damageTargets = useMemo(() => {
    const explicit = (((damageAalPayload as any)?.keys?.target as string[] | undefined) ?? []).filter(Boolean)
    if (explicit.length) return explicit
    if (damageAalPeriodNode && typeof damageAalPeriodNode === 'object') return Object.keys(damageAalPeriodNode)
    return []
  }, [damageAalPayload, damageAalPeriodNode])

  const damageBreakdownHasData = damageTargets.some(target => sumMetricNode(damageAalPeriodNode?.[target]) != null)

  const loading = scoreLoading || damageAalLoading || revenueAalLoading || biDaysLoading
  const allErrored = !!(scoreError && damageAalError && revenueAalError && biDaysError)

  if (loading && !scoreData && !damageAalData && !revenueAalData && !biDaysData) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={160}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (allErrored) {
    return <Alert severity="error">Could not load wildfire summary data.</Alert>
  }

  return (
    <Box>
      <Box
        mb={2}
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}>
        <ClimateSectionTitle title="Overview" subtitle="Summary of wildfire risk for the selected period" />

        {availablePeriods.length > 1 && (
          <ToggleButtonGroup
            size="small"
            value={effectivePeriod}
            exclusive
            onChange={(_, value) => {
              if (value) setSelectedPeriod(value)
            }}>
            {availablePeriods.map(period => (
              <ToggleButton key={period} value={period}>
                {canonicalPeriodLabels[period as CanonicalPeriod]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ClimateMetricCard title="Risk Rating">
            {scoreValue != null ? (
              <WildfireScoreBadge score={scoreValue} size="compact" />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Wildfire risk rating is unavailable for this period.
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ClimateMetricCard title={totalLossDisplay.title}>
            {totalLossDisplay.value != null ? (
              <Typography variant="h4">{formatCurrencyValue(totalLossDisplay.value, currencySymbol, 0)}</Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Annual loss data is unavailable for this period.
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <ClimateMetricCard title={BI_DAYS_LABEL}>
            {biDaysTotal != null ? (
              <Typography variant="h4">{`${formatPlainNumber(biDaysTotal, 1)} days/year`}</Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Business interruption days are unavailable for this period.
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>
      </Grid>

      <Box mt={4}>
        <ClimateSectionTitle
          title={FINANCIAL_BREAKDOWN_TITLE}
          subtitle="Damage and revenue components of annualised wildfire impacts"
        />
      </Box>

      <Grid container spacing={2} mt={0.5}>
        <Grid item xs={12} md={6}>
          <ClimateMetricCard title={DAMAGE_AAL_LABEL}>
            {damageAalTotal != null ? (
              <Typography variant="h4">{formatCurrencyValue(damageAalTotal, currencySymbol, 0)}</Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Damage AAL is unavailable for this period.
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ClimateMetricCard title={REVENUE_AAL_LABEL}>
            {revenueAalTotal != null ? (
              <Typography variant="h4">{formatCurrencyValue(revenueAalTotal, currencySymbol, 0)}</Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Revenue AAL is unavailable for this period.
              </Typography>
            )}
          </ClimateMetricCard>
        </Grid>
      </Grid>

      <Grid container spacing={2} mt={0.5}>
        <Grid item xs={12} md={7}>
          <Card variant="outlined">
            <CardContent>
              <ClimateSectionTitle title="Damage Breakdown" subtitle="Structure and contents split for annualised wildfire damage" />
              <Box mt={2}>
                {!damageBreakdownHasData ? (
                  <Typography variant="body2" color="text.secondary">
                    No damage breakdown is available for this period.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableBody>
                      {damageTargets.map(target => (
                        <TableRow key={target}>
                          <TableCell>{formatDamageTargetLabel(target)}</TableCell>
                          <TableCell align="right">
                            {formatCurrencyValue(sumMetricNode(damageAalPeriodNode?.[target]), currencySymbol, 0)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={5}>
          <Card variant="outlined">
            <CardContent>
              <ClimateSectionTitle title="Wildfire Activity" subtitle="Lower-detail wildfire signal for the selected period" />
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Estimated fires per year
                </Typography>
                <Typography variant="h4">
                  {firesValue != null ? formatPlainNumber(firesValue, 1) : '—'}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
